import { type RouteSectionProps, useNavigate } from "@solidjs/router";
import type { OauthInfo } from "sdk";
import {
	createMemo,
	createResource,
	createSignal,
	ErrorBoundary,
	For,
	type JSX,
	Show,
	type VoidProps,
} from "solid-js";
import { useCtx } from "@/app/context";

export const RouteAuthorize = (_p: RouteSectionProps): JSX.Element => {
	const ctx = useCtx();

	const [data] = createResource(async () => {
		// HACK: openapi fetch typescript doesn't like manually built urls here
		const { data, error } = await ctx.client.http.GET(
			`/api/v1/oauth/authorize${location.search}` as "/api/v1/oauth/authorize",
			{} as any,
		);
		if (error) throw (error as any).error;
		return data;
	});

	return (
		<div class="oauth-authorize">
			<ErrorBoundary
				fallback={(err) => <div class="error">error: {err.message}</div>}
			>
				<Show when={data.loading}>loading...</Show>
				<Show when={data()}>
					{(d) => (
						<OauthAuthorizePrompt
							application={d().application}
							auth_user={d().auth_user}
							bot_user={d().bot_user}
							authorized={d().authorized}
						/>
					)}
				</Show>
			</ErrorBoundary>
		</div>
	);
};

export const OauthAuthorizePrompt = (p: VoidProps<OauthInfo>) => {
	const ctx = useCtx();
	const nav = useNavigate();
	const [authorizing, setAuthorizing] = createSignal(false);

	const authorize = async () => {
		try {
			setAuthorizing(true);
			const { data, error } = await ctx.client.http.POST(
				`/api/v1/oauth/authorize${location.search}` as "/api/v1/oauth/authorize",
				{} as any,
			);
			if (error) {
				console.error(error);
			} else if (data && "redirect_uri" in data) {
				location.href = (data as { redirect_uri: string }).redirect_uri;
			}
		} catch (err) {
			setAuthorizing(false);
			console.error(err);
		}
	};

	const scopes = createMemo(() => {
		const url = URL.parse(location.href);
		if (!url) return [];
		const s = url.searchParams.get("scope");
		const s2 = Array.isArray(s) ? s : s ? [s] : [];
		return s2.flatMap((i) => i.split(" "));
	});

	const cancel = () => {
		window.close();
		nav("/");
	};

	return (
		<div>
			<h2>
				Authorize <b>{p.application.name}</b>
			</h2>

			<Show when={p.application.description}>
				<div class="description">
					<h3 class="dim">app description</h3>
					<div style="height:8px" />
					<div class="contents">{p.application.description}</div>
				</div>
			</Show>

			<div class="info">
				This will grant <b>{p.application.name}</b> access to:
				<ul>
					<For each={scopes()}>{(i) => <li>{getScopeDescription(i)}</li>}</For>
				</ul>
				<div style="height:8px" />
				<div class="dim">
					You are signed in as <b>{p.auth_user.name}</b>
				</div>
			</div>
			<menu>
				<button
					type="button"
					class="button big"
					onClick={cancel}
					disabled={authorizing()}
				>
					cancel
				</button>
				<button
					type="button"
					class="button big primary"
					onClick={authorize}
					disabled={authorizing()}
				>
					{authorizing() ? "authorizing..." : "authorize"}
				</button>
			</menu>
		</div>
	);
};

function getScopeDescription(s: string) {
	switch (s) {
		case "openid":
		case "identify":
			return "basic profile information";
		case "email":
			return "return email address in user profile";
		case "rooms":
			return "list rooms the user is in";
		case "relationships":
			return "list friends the user has";
		case "full":
			return "full access to your account";
		case "auth":
			return "full access, including authorization information";
		default:
			return `!!! UNKNOWN OR INVALID SCOPE "${s}" !!!`;
	}
}
